// ============================================================================
// NOTE DETAIL VIEW (FSA-1)
// ============================================================================
// Print-style preview of a single Note with two regions:
//
//   1. Print rows (Particulars | Note | CY | PY) including recursive
//      sub-classifications and a Total row.
//   2. "Disclosures Required" — translates the boolean disclosure flags
//      stamped on classes into accountant-friendly entries with possible
//      values, using DISCLOSURE_DEFINITIONS.
//
// Drilled into from NotesIndex via a per-note click.

import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  TemplateScheduleNode,
  ScheduleClass,
  AdditionalDisclosure,
  FormatColumnSpec,
} from 'api/types/fsa/template.types';
import {
  parseDisclosureRules,
  parseAdditionalDisclosures,
  parseColumnSpec,
} from 'api/types/fsa/template.types';
import {
  DISCLOSURE_DEFINITIONS,
  type DisclosureDefinition,
} from './disclosureDefinitions';

interface NoteDetailViewProps {
  scheduleNode: TemplateScheduleNode;
  onBack: () => void;
}

interface ResolvedDisclosure {
  /** Source of the disclosure — schedule-level (preferred) or class-level (legacy flag). */
  source: 'schedule' | 'class';
  /** Stable identifier (note-level code OR class-level flag name). */
  key: string;
  label: string;
  description: string;
  possibleValues: string[];
  freeText: boolean;
  /** GRID format — column spec for tabular disclosures. */
  gridColumns: FormatColumnSpec[];
  /** Names of the classes that carry this disclosure (class-level only). */
  classNames: string[];
}

const NoteDetailView: React.FC<NoteDetailViewProps> = ({ scheduleNode, onBack }) => {
  const { schedule, classes } = scheduleNode;

  // Disclosure resolution merges two sources:
  //   1. Schedule-level (post-fix-019) — schedule.additionalDisclosures JSONB
  //      carries label / description / possible_values directly.
  //   2. Class-level (pre-fix-019, still supported) — class.disclosureRules
  //      JSONB has boolean flags translated via DISCLOSURE_DEFINITIONS.
  // Schedule-level entries are listed first (they are authoritative when
  // present); class-level entries appear below.
  const disclosures = useMemo<ResolvedDisclosure[]>(() => {
    const out: ResolvedDisclosure[] = [];

    // 1. Schedule-level (preferred source going forward).
    const scheduleLevel: AdditionalDisclosure[] = parseAdditionalDisclosures(schedule);
    for (const d of scheduleLevel) {
      out.push({
        source: 'schedule',
        key: d.code,
        label: d.label,
        description: d.description,
        possibleValues: d.possible_values ?? [],
        freeText: !!d.free_text,
        gridColumns: d.column_spec ?? [],
        classNames: [],
      });
    }

    // 2. Class-level (legacy boolean flags + frontend mapping).
    const classMap = new Map<string, ResolvedDisclosure>();
    const walk = (cls: ScheduleClass[]) => {
      for (const c of cls) {
        const rules = parseDisclosureRules(c);
        for (const flag of Object.keys(rules)) {
          if (rules[flag] !== true) continue;
          const def: DisclosureDefinition | null = DISCLOSURE_DEFINITIONS[flag] ?? null;
          if (!def) continue;
          const existing = classMap.get(flag);
          if (existing) {
            if (!existing.classNames.includes(c.name)) existing.classNames.push(c.name);
          } else {
            classMap.set(flag, {
              source: 'class',
              key: flag,
              label: def.label,
              description: def.description,
              possibleValues: def.options ?? [],
              freeText: !!def.freeText,
              gridColumns: [],
              classNames: [c.name],
            });
          }
        }
        if (c.children?.length) walk(c.children);
      }
    };
    walk(classes);
    out.push(...Array.from(classMap.values()));

    return out;
  }, [schedule, classes]);

  return (
    <div style={containerStyle}>
      {/* Back nav */}
      <button type="button" onClick={onBack} style={backButtonStyle}>
        <IconSystem.chevronLeft size={14} style={{ marginRight: 4 }} />
        Back to Notes
      </button>

      <header style={headerStyle}>
        <div style={noteIconBadgeStyle}>
          <IconSystem.file size={20} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={subtitleStyle}>NOTE</div>
          <h2 style={titleStyle}>{schedule.name}</h2>
          {schedule.description && <div style={descStyle}>{schedule.description}</div>}
        </div>
      </header>

      {/* Data-fields info strip — shows what columns each row in this note captures. */}
      <DataFieldsStrip schedule={schedule} />

      {/* Print rows — dynamic column layout driven by schedule.columnSpec.
          Classes are grouped by section_label (free-text presentation hint). */}
      <NoteBreakdown classes={classes} columnSpec={parseColumnSpec(schedule)} />

      {/* Disclosures Required */}
      <section style={sectionWrapperStyle}>
        <h3 style={sectionTitleStyle}>Disclosures Required</h3>
        {disclosures.length === 0 ? (
          <div style={emptyDisclosureStyle}>
            No additional disclosures required for this note beyond the line items above.
          </div>
        ) : (
          <div style={disclosureListStyle}>
            {disclosures.map((d) => (
              <DisclosureCard key={`${d.source}:${d.key}`} disclosure={d} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default NoteDetailView;

// ============================================================================
// NOTE BREAKDOWN — dynamic-column print preview
// ============================================================================
// Renders a Particulars + (one cell per column_spec entry) table.
// • Section header rows are visually distinct ("section bar" with thick top
//   rule + uppercase label) so each section reads as a clear group.
// • No implicit end-of-note "Total" row — schedule-level totals come from
//   computed classes within their own sections (e.g., Total Authorised,
//   Total Paid-up).
// • If columnSpec is empty (older notes), falls back to "Particulars / CY / PY".
// • Computed-class rows render bold with a top rule on the amount cells.
// • Structural sub-heading parents (children, not computed) span the full
//   row without amount cells.

const NoteBreakdown: React.FC<{
  classes: ScheduleClass[];
  columnSpec: FormatColumnSpec[];
}> = ({ classes, columnSpec }) => {
  // Group root classes by section_label (free-text presentation hint). When
  // all classes share the same label (e.g. "Default"), the table renders
  // as one continuous list with no section bands. Multi-section Notes
  // (Note 1 Share Capital, Note 2 Reserves) render bands.
  type ClassGroup = { label: string; classes: ScheduleClass[] };
  const groups: ClassGroup[] = [];
  for (const c of classes) {
    const label = (c.sectionLabel ?? 'Default').trim() || 'Default';
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.classes.push(c);
    } else {
      groups.push({ label, classes: [c] });
    }
  }
  const hasMultipleSections = groups.length > 1;

  const fallbackColumns: FormatColumnSpec[] = [
    { code: 'amount_cy', label: 'As at Current Year', data_type: 'amount' },
    { code: 'amount_py', label: 'As at Previous Year', data_type: 'amount' },
  ];
  // Print table only renders columns that are NOT marked as grouping_field.
  // Grouping fields (e.g., share_type) are captured per row at FSA-3 entry but
  // their values drive the visual sub-heading grouping rather than printing
  // as their own column.
  const allCols = columnSpec.length > 0 ? columnSpec : fallbackColumns;
  const dataCols = allCols.filter((c) => !c.is_grouping_field);
  const totalCols = 1 + dataCols.length; // Particulars + printable data columns

  // Build header groups for two-level headers. Consecutive columns sharing
  // the same `group` value merge under one top-row cell. Ungrouped columns
  // get their own slot in the top row with rowSpan=2 so they line up with
  // the bottom-row labels.
  type HeaderGroup = { label: string | null; columns: FormatColumnSpec[] };
  const headerGroups: HeaderGroup[] = [];
  for (const c of dataCols) {
    const last = headerGroups[headerGroups.length - 1];
    if (c.group && last?.label === c.group) {
      last.columns.push(c);
    } else {
      headerGroups.push({ label: c.group ?? null, columns: [c] });
    }
  }
  const hasGroupedHeader = headerGroups.some((g) => g.label !== null);

  return (
    <section style={sectionWrapperStyle}>
      <h3 style={sectionTitleStyle}>Note Breakdown</h3>
      <div style={tableWrapperStyle}>
        <table style={printTableStyle}>
          <colgroup>
            <col />
            {dataCols.map((c) => (
              <col key={c.code} style={{ width: columnWidthFor(c) }} />
            ))}
          </colgroup>
          <thead>
            {hasGroupedHeader ? (
              <>
                <tr>
                  <th rowSpan={2} style={{ ...thStyle, textAlign: 'left', verticalAlign: 'bottom' }}>
                    Particulars
                  </th>
                  {headerGroups.map((g, gi) =>
                    g.label ? (
                      <th
                        key={`grp-${gi}`}
                        colSpan={g.columns.length}
                        style={{ ...thStyle, textAlign: 'center' }}
                      >
                        {g.label}
                      </th>
                    ) : (
                      // Ungrouped columns render directly in the top row
                      // with rowSpan=2 so they align with the second row.
                      g.columns.map((c) => (
                        <th
                          key={c.code}
                          rowSpan={2}
                          style={{ ...thStyle, textAlign: alignFor(c), verticalAlign: 'bottom' }}
                        >
                          {c.label}
                          <div style={thSubLabelStyle}>
                            {c.data_type}{c.is_optional ? ' · optional' : ''}
                          </div>
                        </th>
                      ))
                    )
                  )}
                </tr>
                <tr>
                  {/* Bottom row: only renders the columns that BELONG to a group
                      (their group cell is in the top row). Ungrouped columns
                      already rowSpan=2 in the top row. */}
                  {headerGroups.flatMap((g) =>
                    g.label
                      ? g.columns.map((c) => (
                          <th
                            key={c.code}
                            style={{ ...thStyle, textAlign: alignFor(c), borderTop: 'none' }}
                          >
                            {c.label}
                            <div style={thSubLabelStyle}>
                              {c.data_type}{c.is_optional ? ' · optional' : ''}
                            </div>
                          </th>
                        ))
                      : []
                  )}
                </tr>
              </>
            ) : (
              <tr>
                <th style={{ ...thStyle, textAlign: 'left' }}>Particulars</th>
                {dataCols.map((c) => (
                  <th key={c.code} style={{ ...thStyle, textAlign: alignFor(c) }}>
                    {c.label}
                    <div style={thSubLabelStyle}>{c.data_type}{c.is_optional ? ' · optional' : ''}</div>
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {groups.map((group, idx) => (
              <React.Fragment key={`${group.label}-${idx}`}>
                {/* Section band heading — only when there are multiple
                    distinct section labels. Single-section notes (most of
                    them) skip the band so the table reads as one
                    continuous list. */}
                {hasMultipleSections && group.label && (
                  <tr>
                    <td colSpan={totalCols} style={{
                      ...sectionBandStyle,
                      borderTop: idx === 0 ? '2px solid var(--text-primary)' : '3px solid var(--text-primary)',
                    }}>
                      {idx + 1}. {group.label.toUpperCase()}
                    </td>
                  </tr>
                )}
                {group.classes.map((cls) => (
                  <ClassPrintRow
                    key={cls.id}
                    cls={cls}
                    depth={0}
                    dataCols={dataCols}
                    totalCols={totalCols}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div style={legendStyle}>
        Amounts will populate from the engagement trial balance after classification (FSA-2).
        Section-level totals (where required by Schedule III) are shown as bold rows within each section.
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
// Helpers — column alignment and width hints
// ----------------------------------------------------------------------------

function alignFor(c: FormatColumnSpec): 'left' | 'right' | 'center' {
  if (c.align) return c.align;
  if (c.data_type === 'amount' || c.data_type === 'integer' || c.data_type === 'percent') return 'right';
  if (c.data_type === 'date') return 'center';
  return 'left';
}

function columnWidthFor(c: FormatColumnSpec): number {
  switch (c.data_type) {
    case 'amount':   return 140;
    case 'integer':  return 130;
    case 'percent':  return 100;
    case 'date':     return 120;
    case 'enum':     return 110;
    case 'text':     return 140;
    default:         return 130;
  }
}

// ============================================================================
// CLASS ROW (recursive) — renders Particulars + one cell per column
// ============================================================================

const ClassPrintRow: React.FC<{
  cls: ScheduleClass;
  depth: number;
  dataCols: FormatColumnSpec[];
  totalCols: number;
}> = ({ cls, depth, dataCols, totalCols }) => {
  const hasChildren = cls.children && cls.children.length > 0;
  const isComputed = cls.isComputed;
  const isStructuralSubHeading = hasChildren && !isComputed;

  // Structural sub-heading parent (e.g., "1. Equity Shares" wrapping the
  // 4 reconciliation rows): full-row label, no amount cells.
  if (isStructuralSubHeading) {
    return (
      <>
        <tr>
          <td
            colSpan={totalCols}
            style={{
              ...subHeadingRowStyle,
              paddingLeft: 12 + depth * 16,
            }}
          >
            {cls.name}
          </td>
        </tr>
        {cls.children.map((child) => (
          <ClassPrintRow key={child.id} cls={child} depth={depth + 1} dataCols={dataCols} totalCols={totalCols} />
        ))}
      </>
    );
  }

  return (
    <>
      <tr style={isComputed ? { background: 'var(--bg-secondary)' } : undefined}>
        <td
          style={{
            ...tdLabelStyle,
            paddingLeft: 12 + depth * 16,
            fontWeight: isComputed ? 700 : 400,
          }}
        >
          {cls.name}
        </td>
        {dataCols.map((c) => (
          <td
            key={c.code}
            style={{
              ...tdCellStyle,
              textAlign: alignFor(c),
              fontWeight: isComputed ? 700 : 400,
              borderTop: isComputed ? '1px solid var(--text-primary)' : undefined,
              fontVariantNumeric:
                c.data_type === 'amount' || c.data_type === 'integer' || c.data_type === 'percent'
                  ? 'tabular-nums'
                  : undefined,
            }}
          >
            —
          </td>
        ))}
      </tr>
      {hasChildren && cls.children.map((child) => (
        <ClassPrintRow key={child.id} cls={child} depth={depth + 1} dataCols={dataCols} totalCols={totalCols} />
      ))}
    </>
  );
};

// ============================================================================
// DATA FIELDS STRIP — shows what column_spec each row in the Note will capture
// ============================================================================

const DataFieldsStrip: React.FC<{ schedule: TemplateScheduleNode['schedule'] }> = ({ schedule }) => {
  const cols = useMemo(() => parseColumnSpec(schedule), [schedule]);
  if (cols.length === 0) return null;

  return (
    <div style={dataFieldsStripStyle}>
      <div style={dataFieldsHeaderStyle}>
        <IconSystem.info size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
        Data captured per row
      </div>
      <div style={dataFieldsListStyle}>
        {cols.map((c) => {
          // Type label — includes enum options when applicable so the user
          // sees the actual choices (e.g., "enum: Equity / Preference").
          let typeLabel: string;
          if (c.is_computed) {
            typeLabel = `computed: ${c.formula ?? '?'}`;
          } else if (c.data_type === 'enum' && c.options && c.options.length > 0) {
            typeLabel = `enum: ${c.options.join(' / ')}`;
          } else {
            typeLabel = c.data_type;
          }
          if (c.is_optional) typeLabel += ' · optional';

          return (
            <span key={c.code} style={dataFieldChipStyle}>
              <strong>{c.label}</strong>
              {c.group && <span style={dataFieldGroupStyle}>{c.group}</span>}
              {c.is_grouping_field && <span style={dataFieldGroupingFieldStyle}>drives row grouping</span>}
              <span style={dataFieldTypeStyle}>{typeLabel}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// DISCLOSURE CARD
// ============================================================================

const DisclosureCard: React.FC<{ disclosure: ResolvedDisclosure }> = ({ disclosure }) => {
  const { label, description, possibleValues, freeText, gridColumns, classNames } = disclosure;
  return (
    <div style={disclosureCardStyle}>
      <div style={disclosureHeaderStyle}>
        <h4 style={disclosureLabelStyle}>{label}</h4>
        {classNames.length > 0 && (
          <div style={disclosureScopeStyle} title="Applies to these line items">
            applies to: {classNames.join(', ')}
          </div>
        )}
      </div>
      <div style={disclosureDescriptionStyle}>{description}</div>

      {/* GRID disclosure — render as an empty preview table with column headers + a "+ Add row" placeholder. */}
      {gridColumns.length > 0 ? (
        <div style={{ marginTop: 6 }}>
          <div style={disclosureGridLabelStyle}>Tabular disclosure — capture multiple rows:</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={gridTableStyle}>
              <thead>
                <tr>
                  {gridColumns.map((col) => (
                    <th key={col.code} style={gridThStyle}>
                      {col.label}
                      <div style={gridColumnTypeStyle}>{col.data_type}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {gridColumns.map((col) => (
                    <td key={col.code} style={gridTdStyle}>—</td>
                  ))}
                </tr>
                <tr>
                  <td colSpan={gridColumns.length} style={gridAddRowStyle}>
                    + add row at FSA-3 entry time
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={disclosurePossibleValuesRowStyle}>
          <span style={disclosureFieldLabelStyle}>Possible values:</span>
          {possibleValues.length > 0 ? (
            possibleValues.map((opt) => (
              <span key={opt} style={disclosureChipStyle}>
                {opt}
              </span>
            ))
          ) : freeText ? (
            <span style={freeTextChipStyle}>Free text — specify separately for each occurrence</span>
          ) : (
            <span style={appliesAsRuleStyle}>Applies as a presentation rule (no input required)</span>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const containerStyle: React.CSSProperties = {
  padding: '0.5rem 0',
};

const backButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: '0.3rem 0.6rem',
  background: 'transparent',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  marginBottom: '0.75rem',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'flex-start',
  marginBottom: '1rem',
};

const noteIconBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 44,
  height: 44,
  borderRadius: 6,
  background: '#dbeafe',
  color: '#1e3a8a',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 1,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.05rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

const descStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  marginTop: 4,
  lineHeight: 1.5,
};

const sectionWrapperStyle: React.CSSProperties = {
  marginTop: '1.25rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  color: 'var(--text-secondary)',
  margin: '0 0 0.5rem 0',
  paddingBottom: '0.25rem',
  borderBottom: '1px solid var(--border-color)',
};

const tableWrapperStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  overflow: 'hidden',
};

const printTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
  background: 'var(--bg-primary)',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '2px solid var(--text-primary)',
  fontWeight: 700,
  fontSize: '0.75rem',
  background: 'var(--bg-secondary)',
};

const periodLabelStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 500,
  color: 'var(--text-secondary)',
};

// Section band: distinct visual divider that introduces each Note section
// (Authorised Capital / Issued and Paid up / Reconciliation).
const sectionBandStyle: React.CSSProperties = {
  padding: '0.55rem 0.75rem',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: 0.5,
  background: '#1e3a8a',
  color: 'white',
  borderBottom: '2px solid #1e3a8a',
};

// Sub-heading row inside a section (e.g., "1. Equity Shares" inside Reconciliation).
const subHeadingRowStyle: React.CSSProperties = {
  padding: '0.4rem 0.75rem',
  fontWeight: 600,
  fontSize: '0.8125rem',
  fontStyle: 'italic',
  background: 'var(--bg-secondary)',
  borderBottom: '1px dashed var(--border-color)',
};

// Particulars (left-aligned label) cell.
const tdLabelStyle: React.CSSProperties = {
  padding: '0.35rem 0.75rem',
  borderBottom: '1px dashed var(--border-color)',
};

// Generic data cell — used for any column from columnSpec.
const tdCellStyle: React.CSSProperties = {
  padding: '0.35rem 0.75rem',
  borderBottom: '1px dashed var(--border-color)',
};

// Sub-label under each column header showing the data type (amount, integer, etc.).
const thSubLabelStyle: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 400,
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  marginTop: 2,
  textTransform: 'lowercase',
};

const legendStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

const emptyDisclosureStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  background: 'var(--bg-secondary)',
  border: '1px dashed var(--border-color)',
  borderRadius: 6,
};

const disclosureListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const disclosureCardStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  padding: '0.6rem 0.85rem',
  background: 'var(--bg-primary)',
};

const disclosureHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '0.5rem',
  flexWrap: 'wrap',
  marginBottom: 4,
};

const disclosureLabelStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

const disclosureScopeStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  fontWeight: 500,
};

const disclosureDescriptionStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
  marginBottom: 6,
};

const disclosurePossibleValuesRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
};

const disclosureFieldLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginRight: 4,
};

const disclosureChipStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 4,
  background: '#f5f3ff',
  color: '#5b21b6',
  border: '1px solid #ddd6fe',
};

const freeTextChipStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
};

const appliesAsRuleStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
};

// ----- Data fields strip -----

const dataFieldsStripStyle: React.CSSProperties = {
  marginBottom: '0.75rem',
  padding: '0.5rem 0.75rem',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderLeft: '3px solid #2563eb',
  borderRadius: 4,
};

const dataFieldsHeaderStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  color: 'var(--text-secondary)',
  marginBottom: 4,
};

const dataFieldsListStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
};

const dataFieldChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  fontSize: '0.7rem',
  padding: '3px 8px',
  borderRadius: 4,
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  lineHeight: 1.3,
};

const dataFieldTypeStyle: React.CSSProperties = {
  fontSize: '0.6rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

const dataFieldGroupStyle: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 600,
  color: '#1e3a8a',
  background: '#dbeafe',
  padding: '0 4px',
  borderRadius: 3,
  marginTop: 1,
  alignSelf: 'flex-start',
};

const dataFieldGroupingFieldStyle: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 600,
  color: '#5b21b6',
  background: '#f5f3ff',
  border: '1px solid #ddd6fe',
  padding: '0 4px',
  borderRadius: 3,
  marginTop: 1,
  alignSelf: 'flex-start',
};

// ----- Grid disclosure (preview table) -----

const disclosureGridLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 4,
};

const gridTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.7rem',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
};

const gridThStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color)',
  borderRight: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  fontWeight: 700,
  textAlign: 'left',
  fontSize: '0.7rem',
};

const gridColumnTypeStyle: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 400,
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  marginTop: 2,
};

const gridTdStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRight: '1px solid var(--border-color)',
  borderBottom: '1px dashed var(--border-color)',
  color: 'var(--text-secondary)',
};

const gridAddRowStyle: React.CSSProperties = {
  padding: '5px 8px',
  fontSize: '0.7rem',
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
  textAlign: 'center',
  background: 'var(--bg-secondary)',
};
